// Header
import React, { useState, useEffect } from "react";
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { AppBar, Toolbar, Stack, Typography, Box, IconButton, Snackbar, Alert } from "@mui/material";

//import { ColorModeContext } from '../../context/colorMode';

import nightIcon from '../../assets/images/header/night.png';
import alarmIcon from '../../assets/images/header/alarm.svg';
import noticeIcon from '../../assets/images/header/notice.svg';
import settingIcon from '../../assets/images/header/setting.svg';

import noTextOffIcon from '../../assets/images/radio/noTextOff.png';
import noTextOnIcon from '../../assets/images/radio/noTextOn.png';

import { Menus } from '../menus';
import { Setting } from './setting';
//import { UserSetting } from './userSetting';
import { VersionChecker } from './versionChecker';


import { useInterval } from '../../actions/interval';
import { sysconfigAPI, settingAPI, noticeAPI, notificationAPI } from '../../actions/imsServer';

import { useSelector, useDispatch } from 'react-redux';
import { selectNightMode, setNightMode } from '../../redux/infoSlice';

const StyledAppBar = styled(AppBar)`
  && {
    height: 64px;
    minHeight: 64px;
    background-color: #FDFBFE;
    box-shadow: none;
    border-bottom: 1px solid #E0E0E0;
  }
`;

const StyledToolbar = styled(Toolbar)`
  padding-right: 24px;
`;

export const Header = ({appType, setMenusWidth, setPageWidth}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  //const { colorMode: mode, setColorMode: setMode } = useContext(ColorModeContext);

  const [hasNewNotices, setHasNewNotices] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [isOpenSetting, setIsOpenSetting] = useState(false);
  const [notificationActive, setNotificationActive] = useState(true);
  const [settings, setSettings] = useState({ notificationActive: true });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const dispatch = useDispatch();
  const nightModeInfo = useSelector(selectNightMode);

  useEffect(() => {
    getNightModeInfo();
    fetchSettings();

    if(!window.ReactNativeWebView || appType !== 'commander') {
      checkNewNotices();
      if(appType === 'user') {
        checkNewNotifications();
      }
    }
  }, []);

  useInterval(() => {
    getNightModeInfo();
    if(!window.ReactNativeWebView || appType !== 'commander') {
      checkNewNotices();
      if(appType === 'user') {
        checkNewNotifications();
      }
    }
  }, 60000);

  const fetchSettings = async () => {
    try {
      const meInfo = JSON.parse(localStorage.getItem('meInfo'));
      if (!meInfo) return;

      const settingResponse = await settingAPI.getSettings({
        userId: meInfo.id
      });
      const settingsData = settingResponse.data?.settings || [];
      
      const notificationSetting = settingsData.find(setting => setting.name === 'activePush');
      // 데이터가 없으면 기본값 true로 설정
      setNotificationActive(notificationSetting ? notificationSetting.value === 'true' : true);
      setSettings({
        notificationActive: !!notificationSetting
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      // 에러 발생 시에도 기본값 true로 설정
      setNotificationActive(true);
      setSettings({
        notificationActive: false
      });
    }
  };

  const handleNotificationToggle = async () => {
    const newValue = !notificationActive;
    setNotificationActive(newValue);
    try {
      if (settings.notificationActive) {
        await settingAPI.updateSetting({
          name: 'activePush',
          value: newValue.toString(),
        });
      } else {
        await settingAPI.registerSetting({
          name: 'activePush',
          value: newValue.toString(),
        });
        setSettings(prev => ({ ...prev, notificationActive: true }));
      }
      // WebView 메시지 전송
      if (window.ReactNativeWebView) {
        try {
          const meInfo = JSON.parse(localStorage.getItem('meInfo'));
          if (!meInfo) return;

          const settingResponse = await settingAPI.getSettings({userId: meInfo.id});
          // noticePush 값 추출
          let noticePush = true;
          if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
            const noticeSetting = settingResponse.data.settings.find(s => s.name === 'noticePush');
            noticePush = !(noticeSetting && (noticeSetting.value === false || noticeSetting.value === 'false'));
          }

          window.ReactNativeWebView.postMessage(JSON.stringify({
              type: noticePush && newValue ? 'subscribeTopics' : 'unsubscribeTopics',
          }));
          
        } catch (e) {
          // fallback: 기존 동작 유지 (원하면 생략 가능)
          // window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'subscribeTopics' }));
        }
      }
      // Show success message
      setSnackbar({
        open: true,
        message: newValue ? t('notification_enabled') : t('notification_disabled'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating notification setting:', error);   
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getNightModeInfo = async () => {
    try {
      const meInfo = localStorage.getItem('meInfo') ? JSON.parse(localStorage.getItem('meInfo')) : null;

      if(meInfo && meInfo.grade >= 50) {
        // check night mode
        const sysconfigResponse = await sysconfigAPI.getSysconfigs({
          hospitalId: meInfo.hospitalId    
        });

        // 1. Get night mode config
        const nightConfig = sysconfigResponse.data?.sysconfigs?.find(
          config => config.name === 'nightMode'
        );

        // If nightMode config doesn't exist, disable night mode
        if (!nightConfig) {
          dispatch(setNightMode({isInProgress: false, startTime: null}));
          return;
        }
  
        let nightTimeStart = 21; // 기본값 저녁 9시
        let nightTimeEnd = 6;    // 기본값 아침 6시
  
        if (nightConfig?.value) {
          try {
            const value = JSON.parse(nightConfig.value);
            if (value && typeof value === 'object') {
              if (value.start !== undefined && value.end !== undefined) {
                nightTimeStart = value.start;
                nightTimeEnd = value.end;
              }
            }
          } catch (e) {
            console.error('Error parsing night mode JSON value:', e);
            dispatch(setNightMode({isInProgress: false, startTime: null}));
            return;
          }
        }       
        
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        
        // 2. 시간 계산을 위한 기준시간 설정
        const startHour = parseInt(nightTimeStart)/100;
        const startMinute = parseInt(nightTimeStart)%100;
        const endHour = parseInt(nightTimeEnd)/100;
        const endMinute = parseInt(nightTimeEnd)%100;

        let isInProgress = false;

        // 3. 강제 모드가 설정된 경우
        if (nightConfig?.value) {
          try {
            const value = JSON.parse(nightConfig.value);
            if (value && typeof value === 'object' && value.force) {
              const lastDate = new Date(value.force);

              if (now <= lastDate) {
                // 강제 모드일 때는 sysConfig의 lastDate 필드를 startTime으로 사용
                const startTime = new Date(nightConfig.lastDate).getTime();
                dispatch(setNightMode({isInProgress: true, startTime: startTime}));
                return;
              } else {
                // Clear force value
                const updatedValue = {
                  start: nightTimeStart,
                  end: nightTimeEnd,
                  force: null
                };
                await sysconfigAPI.updateSysconfig({
                  hospitalId: meInfo.hospitalId,
                  name: 'nightMode',
                  value: JSON.stringify(updatedValue)
                });
              }
            }
          } catch (e) {
            console.error('Error parsing night mode force value:', e);
            dispatch(setNightMode({isInProgress: false, startTime: null}));
            return;
          }
        }

        // 4. 일반적인 시간 기반 야간모드 체크
        const currentTime = currentHour * 60 + currentMinute;
        const startTime = startHour * 60 + startMinute;
        const endTime = endHour * 60 + endMinute;

        if (startTime > endTime) {
          // 야간모드가 자정을 걸치는 경우 (예: 21:00 ~ 06:00)
          isInProgress = currentTime >= startTime || currentTime < endTime;
        } else {
          // 야간모드가 같은 날인 경우
          isInProgress = currentTime >= startTime && currentTime < endTime;
        }

        const today = new Date();
        if (currentHour < 12) {
          today.setDate(today.getDate() - 1);
        }
        today.setHours(startHour, startMinute, 0, 0);
        dispatch(setNightMode({isInProgress: isInProgress, startTime: today.getTime()}));
      }
    } catch (error) {
      console.error('Failed to get night mode info:', error);
      dispatch(setNightMode({isInProgress: false, startTime: null}));
    }
  };

  const checkNewNotices = async () => {
    try {
      const meInfo = JSON.parse(localStorage.getItem('meInfo'));
      const hospitalId = meInfo?.hospitalId || 0;
      const grade = Number(meInfo?.grade || 0);

      // 마지막으로 공지사항을 확인한 시간 가져오기
      const lastNoticeCheck = localStorage.getItem('lastNoticeCheck');
      const lastCheckTime = lastNoticeCheck ? new Date(lastNoticeCheck).getTime() : 0;

      const res = await noticeAPI.getNotices({hospitalId: meInfo?.hospitalId > 0 ? '0,'+meInfo?.hospitalId : null, state:'0,2'});
      const notices = Array.isArray(res) ? res : res.data.notices || [];
      
      const hasNew = notices.some(notice => {
        const isHospitalIdValid = notice.hospitalId <= 0 || notice.hospitalId === hospitalId;
        let isTypeValid;
        if (grade < 50) {
          isTypeValid = [0, 1, 3].includes(notice.type);
        } else {
          isTypeValid = [0, 1, 2].includes(notice.type);
        }
        
        // 생성 시간이 마지막 확인 시간보다 이후인지 확인
        const noticeCreateTime = notice.createDate ? new Date(notice.createDate).getTime() : 0;
        const isNewAfterLastCheck = noticeCreateTime > lastCheckTime;
        
        return isNewAfterLastCheck && isHospitalIdValid && isTypeValid;
      });

      setHasNewNotices(hasNew);
    } catch (e) {
      console.error('Error checking new notices:', e);
    }
  };

  const checkNewNotifications = async () => {
    try {
      const meInfo = JSON.parse(localStorage.getItem('meInfo'));
      
      // 마지막으로 알림을 확인한 시간 가져오기
      const lastNotificationCheck = localStorage.getItem('lastNotificationCheck');
      const lastCheckTime = lastNotificationCheck ? new Date(lastNotificationCheck).getTime() : 0;
      
      if(meInfo?.grade === 1) {
        const userNotifications = await notificationAPI.getNotifications({
          userId: meInfo.id,
          state: '0,2'
        });
        
        const hasNew = (userNotifications.data.notifications || []).some(notification => {
          const isTypeValid = [0, 3, 4, 5, 6].includes(notification.type);
          const notificationCreateTime = notification.createDate ? new Date(notification.createDate).getTime() : 0;
          const isNewAfterLastCheck = notificationCreateTime > lastCheckTime;
          
          return isNewAfterLastCheck && isTypeValid;
        });
        
        setHasNewNotifications(hasNew);
      } else {
        const res = await notificationAPI.getNotifications({hospitalId: meInfo?.hospitalId, userId: 0, state: '0,2'});
        const notifications = Array.isArray(res) ? res : res.data.notifications || [];
        
        const hasNew = notifications.some(notification => {
          const isUserValid = notification.userId === 0 || notification.userId === meInfo.id;
          const isHospitalValid = notification.hospitalId === 0 || notification.hospitalId === meInfo.hospitalId;
          const notificationCreateTime = notification.createDate ? new Date(notification.createDate).getTime() : 0;
          const isNewAfterLastCheck = notificationCreateTime > lastCheckTime;
          
          return isNewAfterLastCheck && isUserValid && isHospitalValid;
        });
        
        setHasNewNotifications(hasNew);
      }
    } catch (e) {
      console.error('Error checking new notifications:', e);
    }
  };

  // 공지사항 목록으로 이동할 때 마지막 확인 시간 업데이트
  const handleNoticeListClick = () => {
    localStorage.setItem('lastNoticeCheck', new Date().toISOString());
    setHasNewNotices(false);
    navigate('/notices/notice-list');
  };

  // 알림 목록으로 이동할 때 마지막 확인 시간 업데이트
  const handleNotificationListClick = () => {
    localStorage.setItem('lastNotificationCheck', new Date().toISOString());
    setHasNewNotifications(false);
    navigate('/notices/notification-list');
  };
    
  return (
    <>
      <VersionChecker />
      <StyledAppBar position="sticky">
        <StyledToolbar>
          <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">

            <Menus appType={appType} setMenusWidth={setMenusWidth} setPageWidth={setPageWidth}/>

            <Stack direction="row" alignItems="center" gap={{ xs: 0, sm: 1 }}>
              
              {nightModeInfo.isInProgress &&
              <Box sx={{
                display: 'flex',
                height: '26px',
                padding: { xs: '15px 5px', sm: '10px 30px' },
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '16px',
                backgroundColor: '#FF43AA',
                ml: { xs: 1, sm: 2 },
                mr: { xs: 1, sm: 2 }
              }}>
                <img src={nightIcon} alt="night" />
                <Typography sx={{
                  display: { xs: 'none', sm: 'block' },
                  color: '#FFF', 
                  fontSize: '15px',
                  fontWeight: '500'
                }}>         
                  {t('night_mode_in_progress')}
                </Typography>
              </Box>
              }

              {(() => {
                const meInfo = JSON.parse(localStorage.getItem('meInfo'));
                return meInfo?.grade === 50 && 
                <Box
                  component="img"
                  src={notificationActive ? noTextOnIcon : noTextOffIcon}
                  alt={"notification_toggle"}
                  sx={{ 
                    display: 'flex',
                    height: '20px',
                    alignItems: 'center',
                    mr: { xs: 1, sm: 2 },
                    cursor: 'pointer'
                  }}
                  onClick={handleNotificationToggle}
                />
              })()}

              {appType === 'user' && (
              <Box sx={{ position: 'relative' }}>
                <IconButton 
                  sx={{
                    width: '20px', height: '20px', 
                    mr: { xs: 1, sm: 2 },
                    p:0,
                  }}
                  onClick={handleNotificationListClick}>
                    <img src={alarmIcon} alt={'header_alarm'} sx={{width: '20px', height: '20px', flexShrink: 0, aspectRatio: '1/1'}}/>
                </IconButton>
                {hasNewNotifications && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: { xs: 4, sm: 8 },
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#FF43AA',
                      borderRadius: '50%'
                    }}
                  />
                )}
              </Box>
              )}
            
              {(() => {
                const meInfo = JSON.parse(localStorage.getItem('meInfo'));
                return (meInfo?.grade === 80 || appType === 'user') && (
                  <Box sx={{ position: 'relative',  }}>
                    <IconButton 
                      sx={{width: '20px', height: '20px', 
                        mr: { xs: 1, sm: 2 },
                        p:0,
                      }}
                      onClick={handleNoticeListClick}>
                      <img src={noticeIcon} alt={'notices'} sx={{width: '20px', height: '20px', flexShrink: 0, aspectRatio: '1/1'}}/>   
                    </IconButton>
                    {hasNewNotices && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: { xs: 4, sm: 8 },
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#FF43AA',
                          borderRadius: '50%'
                        }}
                      />
                    )}
                  </Box>
                );
              })()}

              <IconButton 
                sx={{
                  width: '20px', height: '20px',
                  p:0,
                  mr: { xs: 1, sm: 2 }
                }}
                onClick={() => appType === 'user' ? navigate('/settings') : setIsOpenSetting(!isOpenSetting)}
              >
                <img src={settingIcon} alt={t('menu_settings')} sx={{width: '20px', height: '20px', aspectRatio: '1/1'}}/>
                <Setting appType={appType} anchorEl={isOpenSetting} setAnchorEl={setIsOpenSetting}/>
              </IconButton>
              
              {/*
              <Avatar 
                alt="User Avatar" 
                src="https://images.pexels.com/photos/6386956/pexels-photo-6386956.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500" 
                onClick={() => handleUserSettingClick(!isOpenUserSetting)}
                sx={{
                  width: 32,
                  height: 32,
                  cursor: 'pointer'
                }}
              />
              <UserSetting anchorEl={isOpenUserSetting} setAnchorEl={handleUserSettingClick} />
              */}
              
            </Stack>

          </Stack>
        </StyledToolbar>
      </StyledAppBar>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
